import { FC, useRef } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { requireTitle } from './helpers/utils';
import { MWeightByRetailerHeader, translateHeaders } from './helpers/HeadersList';

import Grid from '../Grid';
import { listValidator, numberValidator } from '../Validators';
import { AutoCompleteRenderer, CustomIntervalRenderer } from './helpers/customRenderers';
import { IRetailer } from 'shared/types';
import { useTranslation } from 'helpers/languages/translations';
import { keyBy } from 'lodash';

export const MWeightByRetailer: FC<{
    list: IList;
    toolId: number;
    retailers: IRetailer[];
}> = ({ list, toolId, retailers }) => {
    const hotRef = useRef<React.ElementRef<typeof HotTable> | null>(null);
    const translatedHeaders = translateHeaders(MWeightByRetailerHeader);
    const translate = useTranslation();
    const allRetailersString = translate('list.allRetailers') as string;
    const retailerNames = retailers.map((ret) => ret.name).sort();
    const retailersWithAll = [allRetailersString, ...retailerNames];
    const retailersById = keyBy(retailers, 'id');
    const retailerColumnId = list.columns.findIndex((column) => column.includes('retailer'));

    const newData = list.data.map((row) => {
        const formattedRow = [...row];
        if (formattedRow[retailerColumnId] === 0) formattedRow[retailerColumnId] = allRetailersString;
        else formattedRow[retailerColumnId] = retailersById[row[retailerColumnId] as number]?.name || '';
        return formattedRow;
    });

    const formattedList = { ...list, data: newData };

    const afterValidate = (prevList: IList) => {
        const retailersByName = keyBy(retailers, 'name');
        const newData = prevList.data.map((row) => {
            if (row[0] === translate('list.allRetailers')) row[0] = 0;
            else row[0] = retailersByName[row[0] as string]?.id || null;

            if (row[1] !== '' && row[1] !== null && row[1] !== undefined) {
                row[1] = Number(row[1]);
            }

            return row;
        });
        return {
            ...prevList,
            data: newData,
        };
    };

    return (
        <Grid
            toolId={toolId}
            headers={MWeightByRetailerHeader}
            list={formattedList}
            paramHotRef={hotRef}
            afterValidate={afterValidate}
        >
            <HotColumn
                title={requireTitle(translatedHeaders[0])}
                validator={listValidator(retailersWithAll)}
                source={[allRetailersString, ...retailerNames]}
                type="dropdown"
            >
                <AutoCompleteRenderer list={[allRetailersString, ...retailerNames]} hot-renderer />
            </HotColumn>
            <HotColumn
                type="text"
                validator={numberValidator(false, { min: 1 }, true)}
                allowInvalid={true}
                title={requireTitle(translatedHeaders[1])}
            >
                <CustomIntervalRenderer min={1} hot-renderer />
            </HotColumn>
        </Grid>
    );
};
